/**
 * Tenant Service
 *
 * Type-safe tenant management service using @monorepo/shared schemas
 */

import {
  type CompleteTenantOnboarding,
  CompleteTenantOnboardingSchema,
  type CreateTenant,
  CreateTenantSchema,
  type OnboardingStatusResponse,
  OnboardingStatusResponseSchema,
  type PaginatedTenantsResponse,
  PaginatedTenantsResponseSchema,
  type SetDefaultTenant,
  SetDefaultTenantSchema,
  type SwitchTenant,
  SwitchTenantSchema,
  type Tenant,
  TenantSchema,
  type TenantWithRole,
  TenantWithRoleSchema,
  type UpdateTenant,
  UpdateTenantSchema,
} from '@monorepo/shared/schemas/tenants'
import { del, get, patch, post } from '@/lib/api-client'

export const tenantService = {
  /**
   * Get all tenants for the current user
   */
  async getTenants(params?: {
    page?: number
    limit?: number
    search?: string
    role?: string
  }): Promise<PaginatedTenantsResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.role) queryParams.append('role', params.role)

    const url = `/tenants${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedTenantsResponseSchema)
  },

  /**
   * Get a single tenant by ID
   */
  async getTenant(tenantId: string): Promise<TenantWithRole> {
    return get(`/tenants/${tenantId}`, TenantWithRoleSchema)
  },

  /**
   * Create a new tenant
   */
  async createTenant(data: CreateTenant): Promise<Tenant> {
    const validated = CreateTenantSchema.parse(data)

    return post('/tenants', validated, TenantSchema)
  },

  /**
   * Update tenant details
   */
  async updateTenant(tenantId: string, data: UpdateTenant): Promise<Tenant> {
    const validated = UpdateTenantSchema.parse(data)

    return patch(`/tenants/${tenantId}`, validated, TenantSchema)
  },

  /**
   * Delete a tenant (soft delete)
   */
  async deleteTenant(tenantId: string): Promise<void> {
    await del(`/tenants/${tenantId}`)
  },

  /**
   * Switch active tenant context
   */
  async switchTenant(data: SwitchTenant): Promise<{ message: string }> {
    const validated = SwitchTenantSchema.parse(data)

    const response = await post('/tenants/switch', validated)

    // Update local storage with new tenant context
    localStorage.setItem('current_tenant_id', validated.tenant_id)

    return response as { message: string }
  },

  /**
   * Set default tenant for the current user
   */
  async setDefaultTenant(data: SetDefaultTenant): Promise<{ message: string }> {
    const validated = SetDefaultTenantSchema.parse(data)

    return post('/tenants/set-default', validated)
  },

  /**
   * Check if a tenant slug is available
   */
  async checkSlugAvailability(slug: string): Promise<{ available: boolean }> {
    return get(`/tenants/check-slug/${slug}`)
  },

  /**
   * Get current active tenant from context
   */
  getCurrentTenantId(): string | null {
    return localStorage.getItem('current_tenant_id')
  },

  /**
   * Clear tenant context (used during logout)
   */
  clearTenantContext(): void {
    localStorage.removeItem('current_tenant_id')
    localStorage.removeItem('current_environment_id')
    localStorage.removeItem('currentTenant')
  },

  /**
   * Check if tenant needs onboarding
   */
  async checkOnboarding(): Promise<OnboardingStatusResponse> {
    return get('/tenants/user/check-onboarding', OnboardingStatusResponseSchema)
  },

  /**
   * Complete tenant onboarding by setting tenant name
   */
  async completeTenantOnboarding(
    data: CompleteTenantOnboarding
  ): Promise<{ tenant: Tenant }> {
    const validated = CompleteTenantOnboardingSchema.parse(data)

    return post('/tenants/user/onboard', validated)
  },
}
